import { SettingsManager } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { ManagedWindow, closeWindow } from "src/WindowManager/WindowState";
import { Spinner } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { useEffect, useState } from "react";
import { PDFDocument, rgb } from "pdf-lib";
import { MITDocument, MITDocumentVecMap, MITDocumentNextPage, MITDocumentSquare, MITDocumentTable, MITDocumentText, MITDocumentTitle } from "src/utils/MITDocuments";
import { MitStorage } from "src/managers/MitStorage";
import { ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { ExportData } from "src/managers/ExportData";
import { buffer, featureCollection } from "@turf/turf";
import { geomWithBuffer } from "./ConflictSearchCalculations";

export function ConflictReportDialog({ window }: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();

   const [showSpecificPage] = useState<number | null>(0);
   const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);
   const [blob, setBlob] = useState<Blob | null>(null);
   const [pdfPages, setPDFPages] = useState<number>(0);

   useEffect(() => {
      let resS = MitStorage.getValue("resultAndSearchData");
      generateConflictReport(
         (window?.windowParams?.conflictReportData as typeof resultAndSearchData) ?? resS ?? resultAndSearchData,
         {
            backgroundQuality: 0,
            konflictTables: false,
            ...(window?.windowParams?.additionalData)
         }).then(({ document, pages }) => {
            setArrayBuffer(document);
            setBlob(new Blob([document], { type: "application/pdf" }));
            setPDFPages(pages);
         });
   }, [window?.windowParams?.conflictReportData]);

   useEffect(() => {
      if (window?.windowParams?.conflictReportData) {
         MitStorage.setValue("resultAndSearchData", window?.windowParams.conflictReportData);
      }
   }, [window?.windowParams?.conflictReportData]);

   return (
      <AdvancedDragModal
         PosDefault={{ left: "50%", top: "0px", width: "50%", height: "100%", transform: "translate(-50%, 0)" }}
         title={window?.windowParams?.additionalData?.customTitle ?? "Konfliktsøgningsrapport"}
         onClose={() => windowDispatch(closeWindow(window.id))}
         subHeaderContent={
            <>
               <ViewButtonIcon
                  title="Clear result test data"
                  onClick={() =>
                     MitStorage.clearValue("resultAndSearchData")
                  }
               >
                  <MIBIcon icon="debugging" />
               </ViewButtonIcon>
               <ViewButtonIcon
                  title="Download PDF"
                  onClick={() => {
                     if (blob) {
                        ExportData.downloadBlob("conflict_report.pdf", blob);
                     }
                  }}
               >
                  <MIBIcon icon="download" />
               </ViewButtonIcon>
            </>
         }
      >
         <ADModalBody>
            {arrayBuffer ?
               <Document file={arrayBuffer} className={"DatarapportPDFViewer"} >
                  {showSpecificPage ?
                     <Page pageIndex={showSpecificPage} key={showSpecificPage} />
                     : Array.from({ length: pdfPages }, (_, i) => i).map((pageIndex) => (
                        <Page pageIndex={pageIndex} key={pageIndex} />
                     ))}
               </Document>
               : <Spinner />}
         </ADModalBody>
      </AdvancedDragModal >
   );
}

async function generateConflictReport(
   searchData: typeof resultAndSearchData,
   options?: {
      customTitle?: string,
      konflictTables?: boolean,
      backgroundQuality?: number,
   }): Promise<{ pages: number, document: ArrayBuffer }> {
   // MARK: Version
   let pdf = await PDFDocument.create();
   let document = new MITDocument(pdf);

   // let searchData = (await MitStorage.getValue("resultAndSearchData") ?? resultAndSearchData) as typeof resultAndSearchData;
   // HEADER
   document.setTitle(options?.customTitle ?? "Konfliktsøgningsrapport");
   document.setMVersion(process.env.packageContentVersion ?? "dev");
   document.setDate(new Date());
   document.setPageNr("Side: {i} / {t}")

   // Coverpage
   document.add(new MITDocumentSquare(0, 150));
   document.add(new MITDocumentTitle(options?.customTitle ?? "Konfliktsøgningsrapport")
      .setTextSize(36)
   );
   document.add(new MITDocumentText("Genereret d. " + new Date().toLocaleString()));
   document.add(new MITDocumentSquare(0, 50));

   document.add(new MITDocumentNextPage());
   // Indholdsfortegnelse

   // Ansvars fraskrivelse og metode beskrivelse
   let conflictReportDisclaimer = SettingsManager.getSystemSetting("ConflictReportDisclaimer", false);
   if (conflictReportDisclaimer) {
      document.add(new MITDocumentTitle("Metodebeskrivelse"));
      document.add(new MITDocumentText("Konfliktsøgning er udført ved at sammenligne geometrien af de valgte lag i det område, der er defineret af den indsendte geometri og buffer. For hver konflikt, der er fundet, er der genereret en side i denne rapport, der indeholder detaljer om konflikten, herunder det lag, konflikten blev fundet i, og en beskrivelse af konflikten baseret på de tilgængelige attributter for det pågældende lag."));
      document.add(new MITDocumentSquare(0, 12));
   }

   if (conflictReportDisclaimer) {
      document.add(new MITDocumentTitle("Ansvarsfraskrivelse"));
      document.add(new MITDocumentText("Denne rapport er genereret automatisk og kan indeholde fejl. Rapporten bør ikke bruges som eneste kilde til beslutningstagning, og det anbefales at gennemgå de identificerede konflikter manuelt for at sikre nøjagtigheden."));
      document.add(new MITDocumentText("Mapit, Viamap fraskriver sig ethvert ansvar for eventuelle fejl eller unøjagtigheder i denne rapport, og for eventuelle beslutninger, der træffes baseret på denne rapport."));
      document.add(new MITDocumentText("Finder du fejl eller unhensig   tsmæssigheder i rapporten, så kontakt os venligst med en beskrivelse af problemet og, hvis muligt, en kopi af den genererede rapport. Så kan vi bruge det til at forbedre kvaliteten af rapporterne fremover og sikre at infomationen bliver formidlet viderer til andre brugere af Mapit."));
   }
   document.add(new MITDocumentSquare(0, 12));

   // Søge området og søge parametre
   document.add(new MITDocumentTitle("Søgeområde og søgeparametre"));

   // document.add(new MITDocumentText("Søgeområde: " + JSON.stringify(resultAndSearchDataDebug.searchParams.geom)));
   let searchArea: any[] = [];
   document.add(new MITDocumentText("Buffer: " + searchData.searchParams.buffer + " meter"));
   if (searchData.searchParams.geom) {
      let tableData = searchData.searchParams.geom.features?.map((ft) => ft.properties) ?? [];
      document.add(new MITDocumentTable(tableData));
      let fc = geomWithBuffer(searchData.searchParams.geom, searchData.searchParams.buffer ?? 0);
      searchData.searchParams.geom["pdfStyle"] = { borderColor: rgb(0, 0.8, 0.8), borderWidth: 2, borderOpacity: 0.7, size: 5 };
      fc["pdfStyle"] = { borderColor: rgb(0, 0.8, 0.8), borderWidth: 1, borderOpacity: 0.7, borderDashArray: [3, 6] };
      document.add(new MITDocumentVecMap([fc, searchData.searchParams.geom])
         .setImage(options?.backgroundQuality ?? 0)
         .setSignature([
            { title: "Søgeområde", style: { borderColor: rgb(0, 0.8, 0.8), borderWidth: 2, borderOpacity: 0.7 } },
            { title: "Søgeområde buffer", style: { borderColor: rgb(0, 0.8, 0.8), borderWidth: 1, borderOpacity: 0.7, borderDashArray: [3, 6] } }
         ])
      );

      searchArea = [fc, searchData.searchParams.geom];
   }

   // document.add(new MITDocumentSquare(0, 50));

   // Oversigt konflikter og antal
   document.add(new MITDocumentTitle("Oversigt over konflikter"));
   document.add(new MITDocumentSquare(0, 10));

   // PDF tabel 
   let tableData = searchData.results.map((e) => {
      let { source, layer, matchedCount } = e
      return {
         source, layer, matchedCount
      }
   })
   document.add(new MITDocumentTable(tableData));

   // Detaljerede sider for hver konflikt
   searchData.results.filter((a) => a.conflict?.features?.length).forEach((result, index) => {
      addConflictPage(result, document, index);
   });

   return await document.save().then((pdfBytes) => {
      return { pages: pdf.getPageCount(), document: pdfBytes.buffer as ArrayBuffer };
   });

   function addConflictPage(result: typeof resultAndSearchData["results"][number], document: MITDocument, index: number) {
      // Title 
      document.add(new MITDocumentNextPage());
      document.add(new MITDocumentText(`Konflikt ${index + 1}: ${result.layer}`).setTextSize(16));

      if (options?.konflictTables) {
         document.add(new MITDocumentTable(result.conflict?.features?.map((ft) => ft.properties) ?? []));
      }

      document.add(new MITDocumentVecMap([...searchArea, result.conflict])
         .setImage(options?.backgroundQuality ?? 0)
         .setSignature([
            { title: "Søgeområde", style: { borderColor: rgb(0, 0.8, 0.8), borderWidth: 2, borderOpacity: 0.7 } },
            { title: "Søgeområde buffer", style: { borderColor: rgb(0, 0.8, 0.8), borderWidth: 1, borderOpacity: 0.7, borderDashArray: [3, 6] } },
            { title: "Konfliktgeometri", style: { color: rgb(0.8, 0, 0), opacity: 0.5 } },
         ])
      );
      // 
   }
}

const resultAndSearchData = {
   "results": [
      {
         "source": "Matriklen2",
         "layer": "SamletFastEjendom_Gaeldende",
         "conflict": {
            "type": "FeatureCollection",
            "features": [
               {
                  "type": "Feature",
                  "id": "",
                  "geometry": {
                     "type": "MultiPolygon",
                     "coordinates": [
                        [
                           [
                              [
                                 12.178904285221392,
                                 55.73895969099902
                              ],
                              [
                                 12.178984503161521,
                                 55.73699639444501
                              ],
                              [
                                 12.180198458434793,
                                 55.737021085575655
                              ],
                              [
                                 12.180214577161198,
                                 55.73645067646959
                              ],
                              [
                                 12.180866575766718,
                                 55.736062694699015
                              ],
                              [
                                 12.180955362460203,
                                 55.73603019511066
                              ],
                              [
                                 12.181046227709768,
                                 55.7360173575495
                              ],
                              [
                                 12.181404349397695,
                                 55.73601238267009
                              ],
                              [
                                 12.18153973979234,
                                 55.7335333016256
                              ],
                              [
                                 12.181461678364654,
                                 55.73348629126093
                              ],
                              [
                                 12.181506836833353,
                                 55.733489067561976
                              ],
                              [
                                 12.181686644780303,
                                 55.73350450940674
                              ],
                              [
                                 12.181724668981937,
                                 55.73350713791167
                              ],
                              [
                                 12.181661588333073,
                                 55.73354021250746
                              ],
                              [
                                 12.181526680546677,
                                 55.73601592786101
                              ],
                              [
                                 12.181992412519298,
                                 55.73639433606854
                              ],
                              [
                                 12.182489834080615,
                                 55.73646130236274
                              ],
                              [
                                 12.183773837391916,
                                 55.73647049431738
                              ],
                              [
                                 12.184748306895841,
                                 55.736493727710474
                              ],
                              [
                                 12.185110224162738,
                                 55.73653936752579
                              ],
                              [
                                 12.185503095018843,
                                 55.73672797778816
                              ],
                              [
                                 12.185847386279013,
                                 55.73691192299706
                              ],
                              [
                                 12.185846642223588,
                                 55.73715061098778
                              ],
                              [
                                 12.188849230968254,
                                 55.73716956213771
                              ],
                              [
                                 12.189023993176711,
                                 55.73742282254993
                              ],
                              [
                                 12.189225416691345,
                                 55.73771726746912
                              ],
                              [
                                 12.189439539693414,
                                 55.73803215900593
                              ],
                              [
                                 12.189625165915938,
                                 55.73830576940443
                              ],
                              [
                                 12.189836112152934,
                                 55.73861660726502
                              ],
                              [
                                 12.18203712068225,
                                 55.73886147541713
                              ],
                              [
                                 12.178904285221392,
                                 55.73895969099902
                              ]
                           ]
                        ]
                     ]
                  },
                  "properties": {
                     "forretningshaendelse": "Afledt ændring",
                     "senesteSagLokalId": "20124935",
                     "forretningsproces": "Matrikulær sag",
                     "id.namespace": "http://data.gov.dk/Matriklen/SamletFastEjendom",
                     "id.lokalId": "8675099",
                     "registreringFra": "2018-06-03T04:30:42.004Z",
                     "virkningFra": "2001-03-11T23:00:00Z",
                     "virkningsaktoer": "Geodatastyrelsen",
                     "BFEnummer": "8675099",
                     "arbejderbolig": "false",
                     "erFaelleslod": "false",
                     "hovedejendomOpdeltIEjerlejligheder": "false",
                     "landbrugsnotering": "Landbrug",
                     "udskiltVej": "false"
                  }
               }
            ],
            "metadata": {
               "numberMatched": "1",
               "numberReturned": "1",
               "timeStamp": "2026-04-07T14:11:13.589Z",
               "feature": "SamletFastEjendom_Gaeldende",
               "endpoint": "Matriklen2"
            }
         },
         "intersect": null,
         "progress": 1,
         "err": "",
         "matchedCount": 1
      },
      {
         "source": "Matriklen2",
         "layer": "Matrikelskel_Gaeldende",
         "conflict": {
            "type": "FeatureCollection",
            "features": [],
            "metadata": {
               "numberMatched": "0",
               "numberReturned": "0",
               "timeStamp": "2026-04-07T14:11:13.674Z",
               "feature": "Matrikelskel_Gaeldende",
               "endpoint": "Matriklen2"
            }
         },
         "intersect": null,
         "progress": 1,
         "err": "",
         "matchedCount": 0
      },
      {
         "source": "Matriklen2",
         "layer": "Skelpunkt_Gaeldende",
         "conflict": {
            "type": "FeatureCollection",
            "features": [],
            "metadata": {
               "numberMatched": "0",
               "numberReturned": "0",
               "timeStamp": "2026-04-07T14:11:13.739Z",
               "feature": "Skelpunkt_Gaeldende",
               "endpoint": "Matriklen2"
            }
         },
         "intersect": null,
         "progress": 1,
         "err": "",
         "matchedCount": 0
      }
   ],
   "searchParams": {
      "id": "profile_test",
      "name": "Test Profile",
      "layers": [
         {
            "source": "Matriklen2",
            "layer": "SamletFastEjendom_Gaeldende"
         },
         {
            "source": "Matriklen2",
            "layer": "Matrikelskel_Gaeldende"
         },
         {
            "source": "Matriklen2",
            "layer": "Skelpunkt_Gaeldende"
         }
      ],
      "feature": "Debugging",
      "geom": {
         "type": "FeatureCollection",
         "features": [
            {
               "type": "Feature",
               "properties": {
                  "Navn": "Østrupvej 160",
                  "Postnummer": "3670 Veksø Sjælland",
                  "Kommunekode": "0265"
               },
               "geometry": {
                  "type": "Point",
                  "coordinates": [
                     12.17999306928101,
                     55.738419961034
                  ]
               }
            }
         ]
      },
      "buffer": 0
   }
} as const;

